import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, apiImageUrl } from '../../utils/streamApi';
import IndustrialLayout, { toast } from './IndustrialLayout';
import { makeSimpleRing } from '../../utils/polygon';

// ── Seat Zones ────────────────────────────────────────────────────────────────
// Draw one polygon per desk and name it after whoever sits there. The backend's
// seat_occupancy detector then samples the camera on a slow wall-clock cadence and
// records how long each seat was actually occupied — the Seat Occupancy report.
//
// Draw the polygon around where the PERSON appears, not around the chair. On a
// typical office camera the people across the table are visible from the chest up,
// so the box that matters covers head and torso. Membership is decided by the
// centre of the detected person box (the same centroid rule every other zone in
// the system uses), and the live preview draws that dot so it is never a guess.

const DEF = {
  seats: [],
  sample_secs: 10,
  confirm_samples: 2,
  window_samples: 3,
  merge_gap_secs: 180,
  checkpoint_secs: 300,
  conf: 0.35,
  imgsz: 640,
  work_start: '09:30',
  work_end: '18:30',
  skip_outside_hours: true,
};

export default function IndustrialSeatsPage() {
  const [cameras, setCameras] = useState([]);
  const [scope, setScope]     = useState(null);
  const [cfg, setCfg]         = useState(DEF);
  const [enabled, setEnabled] = useState([]);
  const [dirty, setDirty]     = useState(false);
  const [saving, setSaving]   = useState(false);
  const [active, setActive]   = useState(-1);     // seat highlighted on the canvas (hover)
  // Which seat the next "Finish seat" REPLACES, or -1 to add a new one. Kept apart
  // from `active` on purpose: highlighting follows the mouse over the seat list, so
  // folding the two together made every finished polygon overwrite the highlighted
  // seat instead of adding the next one.
  const [redrawIdx, setRedrawIdx] = useState(-1);
  const [live, setLive]       = useState(false);  // poll the server-annotated frame
  const [imgUrl, setImgUrl]   = useState(null);
  const [imgErr, setImgErr]   = useState(false);
  const [pts, setPts]         = useState([]);     // in-progress normalized points

  const canvasRef = useRef(null);
  const imgRef    = useRef(null);
  const prevUrl   = useRef(null);
  const nav       = useNavigate();

  // No extra role check here. Reaching this page already required the
  // `industrial-seats` menu, which is admin-only by default (it is deliberately
  // absent from _OPERATIONAL_MENUS) — so a site that grants it to a custom role
  // means it. Gating the drawing surface on isAdmin() as well silently made the
  // page read-only for exactly those custom roles, and the backend save only
  // requires an authenticated user anyway.
  const camName = cameras.find(c => c.idx === scope)?.name || (scope != null ? `Camera ${scope}` : '');
  // Stable identity — the canvas redraw effect depends on it, and a fresh [] every
  // render would repaint the overlay on every keystroke in the seat-name fields.
  const seats   = useMemo(() => cfg.seats || [], [cfg.seats]);

  // ── load ────────────────────────────────────────────────────────────────────
  const selectCamera = useCallback((idx, camList) => {
    const cams = camList || cameras;
    const cam  = cams.find(c => c.idx === idx);
    const saved = cam?.detector_config?.seat_occupancy;
    setScope(idx);
    setEnabled([...(cam?.detectors || [])]);
    setCfg({ ...DEF, ...(saved || {}), seats: (saved?.seats || []).map(s => ({ ...s })) });
    setPts([]); setActive(-1); setRedrawIdx(-1); setDirty(false); setImgErr(false);
    apiImageUrl(`/cameras/${idx}/frame`).then(u => setImgUrl(u)).catch(() => setImgUrl(null));
  }, [cameras]);

  useEffect(() => {
    apiFetch('/ui/cameras').then(r => r.json()).then(d => {
      const list = d.cameras || [];
      setCameras(list);
      if (list.length) selectCamera(list[0].idx, list);
    }).catch(() => {});
  }, []);  // eslint-disable-line

  // Live annotated frame — server draws the seats, the person boxes and the
  // centroid dot that decides membership, so "why is this seat not registering?"
  // is answerable without reading any code.
  useEffect(() => {
    if (!live || scope == null) return;
    let alive = true;
    const tick = () => apiImageUrl(`/ui/cameras/${scope}/annotated?mode=seats&t=${Date.now()}`).then(u => {
      if (!alive) { URL.revokeObjectURL(u); return; }
      setImgUrl(u); setImgErr(false);
      if (prevUrl.current) URL.revokeObjectURL(prevUrl.current);
      prevUrl.current = u;
    }).catch(() => {});
    tick(); const iv = setInterval(tick, 2000);
    return () => {
      alive = false; clearInterval(iv);
      if (prevUrl.current) { URL.revokeObjectURL(prevUrl.current); prevUrl.current = null; }
    };
  }, [live, scope]);

  // ── canvas ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const cv = canvasRef.current, im = imgRef.current;
    if (!cv || !im || !im.complete) return;
    const W = cv.width, H = cv.height, ctx = cv.getContext('2d');
    ctx.clearRect(0, 0, W, H);
    const dot = (p, color) => { ctx.fillStyle = color; ctx.beginPath(); ctx.arc(p[0] * W, p[1] * H, 6, 0, 7); ctx.fill(); };

    seats.forEach((s, i) => {
      const poly = s.polygon || [];
      if (poly.length < 3) return;
      const on = i === active;
      ctx.strokeStyle = on ? '#22c55e' : '#3b82f6';
      ctx.fillStyle   = on ? 'rgba(34,197,94,0.22)' : 'rgba(59,130,246,0.16)';
      ctx.lineWidth   = on ? 3 : 2;
      ctx.beginPath();
      poly.forEach((p, j) => j ? ctx.lineTo(p[0] * W, p[1] * H) : ctx.moveTo(p[0] * W, p[1] * H));
      ctx.closePath(); ctx.fill(); ctx.stroke();
      if (on) poly.forEach(p => dot(p, '#22c55e'));
      ctx.fillStyle = on ? '#86efac' : '#93c5fd';
      ctx.font = 'bold 12px sans-serif';
      ctx.fillText(`💺 ${s.name || `Seat ${i + 1}`}`, poly[0][0] * W + 4, poly[0][1] * H - 5);
    });

    if (pts.length) {
      ctx.strokeStyle = '#f59e0b'; ctx.lineWidth = 2;
      ctx.beginPath();
      pts.forEach((p, i) => i ? ctx.lineTo(p[0] * W, p[1] * H) : ctx.moveTo(p[0] * W, p[1] * H));
      ctx.stroke();
      // Closing edge, dashed: the shape you are about to commit is a RING, and
      // drawing it open hid both the real outline and any crossed edges.
      if (pts.length >= 3) {
        ctx.save();
        ctx.setLineDash([5, 4]); ctx.strokeStyle = '#f59e0b'; ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(pts[pts.length - 1][0] * W, pts[pts.length - 1][1] * H);
        ctx.lineTo(pts[0][0] * W, pts[0][1] * H);
        ctx.stroke();
        ctx.restore();
      }
      // Number the points so the click ORDER is visible — a bowtie is otherwise
      // indistinguishable from a rectangle until it is too late.
      pts.forEach((p, i) => {
        dot(p, '#f59e0b');
        ctx.fillStyle = '#1f2937'; ctx.font = 'bold 10px sans-serif';
        ctx.fillText(String(i + 1), p[0] * W - 3, p[1] * H + 3.5);
      });
    }
  }, [imgUrl, seats, pts, active]);

  const onCanvasClick = (e) => {
    const cv = canvasRef.current; if (!cv) return;
    const r = cv.getBoundingClientRect();
    const p = [(e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height];
    setPts(prev => [...prev, p]);   // functional: two clicks in one batch both land
  };

  // ── seat edits ──────────────────────────────────────────────────────────────
  const patch = (k, v) => { setCfg(c => ({ ...c, [k]: v })); setDirty(true); };
  const patchSeat = (i, k, v) => {
    setCfg(c => ({ ...c, seats: (c.seats || []).map((s, j) => j === i ? { ...s, [k]: v } : s) }));
    setDirty(true);
  };

  const finishSeat = () => {
    if (pts.length < 3) { toast('Click at least 3 points around the person’s seat.', 'err'); return; }
    // Corners clicked diagonally cross into a bowtie, which ray-casting membership
    // reads as the wrong area. Straighten it and say so.
    const { poly, fixed } = makeSimpleRing(pts);
    if (fixed) toast('Those corners crossed over — reordered them into a clean shape.', 'warn');
    if (redrawIdx >= 0 && seats[redrawIdx]) {     // explicit Redraw only
      patchSeat(redrawIdx, 'polygon', poly);
      setPts([]); setRedrawIdx(-1);
      if (!fixed) toast(`“${seats[redrawIdx].name}” redrawn.`, 'ok');
      return;
    }
    const name = `Seat ${seats.length + 1}`;
    setCfg(c => ({ ...c, seats: [...(c.seats || []), { name, polygon: poly }] }));
    setActive(seats.length);
    setPts([]); setDirty(true);
    if (!fixed) toast(`Seat ${seats.length + 1} added — name it, then draw the next one.`, 'ok');
  };

  const removeSeat = (i) => {
    if (!window.confirm(`Remove “${seats[i]?.name || `Seat ${i + 1}`}”? Times already recorded are kept.`)) return;
    setCfg(c => ({ ...c, seats: (c.seats || []).filter((_, j) => j !== i) }));
    setActive(-1); setRedrawIdx(-1); setDirty(true);
  };

  const save = async () => {
    if (scope == null) return;
    // Auto-commit an in-progress polygon — the drawn-but-not-committed trap.
    let next = seats;
    if (pts.length >= 3) {
      next = [...seats, { name: `Seat ${seats.length + 1}`, polygon: makeSimpleRing(pts).poly }];
    }
    if (!next.length) { toast('Draw at least one seat first.', 'err'); return; }
    const dupe = next.map(s => (s.name || '').trim().toLowerCase())
      .find((n, i, a) => n && a.indexOf(n) !== i);
    if (dupe) { toast(`Two seats are both named “${dupe}” — names identify the report rows.`, 'err'); return; }

    setSaving(true);
    try {
      const dets = enabled.includes('seat_occupancy') ? enabled : [...enabled, 'seat_occupancy'];
      const body = {
        detectors: dets,
        detector_config: { seat_occupancy: { ...cfg, seats: next } },
      };
      const r = await apiFetch(`/cameras/${scope}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error();
      setCfg(c => ({ ...c, seats: next })); setPts([]); setEnabled(dets); setDirty(false);
      toast(`Saved — ${next.length} seat${next.length === 1 ? '' : 's'} monitored on ${camName}.`, 'ok');
    } catch { toast('Save failed.', 'err'); }
    finally { setSaving(false); }
  };

  const disable = async () => {
    if (!window.confirm(`Stop measuring seat occupancy on ${camName}? The drawn seats are kept.`)) return;
    const dets = enabled.filter(d => d !== 'seat_occupancy');
    try {
      const r = await apiFetch(`/cameras/${scope}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ detectors: dets }),
      });
      if (!r.ok) throw new Error();
      setEnabled(dets);
      toast('Seat occupancy switched off for this camera.', 'warn');
    } catch { toast('Could not switch it off.', 'err'); }
  };

  const on = enabled.includes('seat_occupancy');

  return (
    <IndustrialLayout eyebrow="Seats" title="Seat Zones"
      subtitle={`${camName.toUpperCase()} · ${seats.length} SEAT${seats.length === 1 ? '' : 'S'}${on ? ' · MEASURING' : ''}`}>

      <div className="ind-card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
          <span className="ind-eye">Camera</span>
          <select value={scope ?? ''} style={sel}
            onChange={e => { if (dirty && !window.confirm('Discard unsaved changes?')) return; selectCamera(Number(e.target.value)); }}>
            {cameras.map(c => <option key={c.idx} value={c.idx}>🎥 {c.name || `Camera ${c.idx}`}</option>)}
          </select>
          <button className={`ind-pill${live ? ' on' : ''}`} onClick={() => setLive(l => !l)}>
            {live ? '⏸ Pause live' : '▶ Live check'}
          </button>
          <button className="ind-pill" onClick={() => { setLive(false); setImgErr(false); apiImageUrl(`/cameras/${scope}/frame`).then(setImgUrl).catch(() => setImgErr(true)); }}>
            ↻ Refresh frame
          </button>
          <span style={{ flex: 1 }} />
          <span className={`ind-pill${on ? ' on' : ''}`} style={{ cursor: 'default' }}>
            {on ? '● Measuring' : '○ Not enabled'}
          </span>
        </div>

        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* LEFT — the frame + drawing surface */}
          <div style={{ flex: '1 1 460px', minWidth: 320 }}>
            <div style={{ position: 'relative', width: '100%', minHeight: 280, borderRadius: 8, overflow: 'hidden',
              border: '1px solid var(--ind-line)', background: 'var(--ind-ink)', display: 'grid', placeItems: 'center' }}>
              {imgUrl && !imgErr
                ? <img ref={imgRef} src={imgUrl} alt="" onError={() => setImgErr(true)}
                    onLoad={() => { const cv = canvasRef.current, im = imgRef.current; if (cv && im) { cv.width = im.clientWidth; cv.height = im.clientHeight; setPts(p => [...p]); } }}
                    style={{ width: '100%', display: 'block' }} />
                : <div className="ind-empty" style={{ padding: 24, textAlign: 'center', maxWidth: 420 }}>
                    {imgErr
                      ? <>📷 Couldn’t load a frame from <b>{camName}</b>.<br />The camera may be offline. Try <b>↻ Refresh frame</b>.</>
                      : <>📷 Pick a camera to load a still to draw on.</>}
                  </div>}
              {imgUrl && !imgErr &&
                <canvas ref={canvasRef} onClick={onCanvasClick}
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', cursor: 'crosshair' }} />}
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <button className="ind-pill on" onClick={finishSeat} disabled={pts.length < 3}>
                ✓ Finish seat ({pts.length} pts)
              </button>
              {pts.length > 0 && <button className="ind-pill" onClick={() => setPts([])}>Clear points</button>}
              <span style={{ fontSize: 12, color: 'var(--ind-tx3)' }}>
                {redrawIdx >= 0 && seats[redrawIdx]
                  ? `Redrawing “${seats[redrawIdx].name}” — click new corners, then Finish seat.`
                  : seats.length
                    ? `Click the corners for seat ${seats.length + 1}, then Finish seat.`
                    : 'Click the corners around a seated person (head and chest), then Finish seat.'}
              </span>
            </div>

            {live && (
              <p style={{ fontSize: 11, color: 'var(--ind-tx3)', marginTop: 8, lineHeight: 1.6 }}>
                Green outline = a person’s centre point is inside that seat right now. The green dot on
                each person is the point that decides it — if the dot sits outside the polygon, the seat
                does not register, so stretch the polygon to cover it.
              </p>
            )}
          </div>

          {/* RIGHT — seat list + settings */}
          <div style={{ flex: '1 1 320px', minWidth: 280 }}>
            <span className="ind-eye">Seats on this camera</span>
            <div style={{ border: '1px solid var(--ind-line)', borderRadius: 8, marginTop: 8, overflow: 'hidden' }}>
              {seats.length === 0 && <div className="ind-empty" style={{ padding: 18, fontSize: 12 }}>No seats drawn yet.</div>}
              {seats.map((s, i) => (
                <div key={i} onMouseEnter={() => setActive(i)}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
                    borderTop: i ? '1px solid var(--ind-line)' : 'none',
                    background: i === active ? 'rgba(34,197,94,0.08)' : 'transparent' }}>
                  <span style={{ fontSize: 14 }}>💺</span>
                  <input value={s.name || ''}
                    onChange={e => patchSeat(i, 'name', e.target.value)}
                    placeholder={`Seat ${i + 1}`}
                    style={{ ...sel, flex: 1, minWidth: 0 }} />
                  <span style={{ fontSize: 10, color: 'var(--ind-tx3)', fontFamily: 'var(--ind-mono)' }}>
                    {(s.polygon || []).length}p
                  </span>
                  <button className={`ind-pill${redrawIdx === i ? ' on' : ''}`} style={{ padding: '2px 8px' }}
                    onClick={() => { setActive(i); setRedrawIdx(redrawIdx === i ? -1 : i); setPts([]); }}>
                    {redrawIdx === i ? 'Cancel' : 'Redraw'}
                  </button>
                  <button className="ind-pill" style={{ padding: '2px 8px' }}
                    onClick={() => removeSeat(i)}>✕</button>
                </div>
              ))}
            </div>
            <p style={{ fontSize: 11, color: 'var(--ind-tx3)', marginTop: 6, lineHeight: 1.6 }}>
              Name each seat after the person who sits there — the name is the report row.
              If people swap desks, the report follows the <b>desk</b>, not the person.
            </p>

            <div style={{ marginTop: 16 }}>
              <span className="ind-eye">Working hours</span>
              <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <input type="time" value={cfg.work_start || ''}
                  onChange={e => patch('work_start', e.target.value)} style={sel} />
                <span style={{ color: 'var(--ind-tx3)', fontSize: 12 }}>to</span>
                <input type="time" value={cfg.work_end || ''}
                  onChange={e => patch('work_end', e.target.value)} style={sel} />
              </div>
              <label style={lbl}>
                <input type="checkbox" checked={!!cfg.skip_outside_hours}
                  onChange={e => patch('skip_outside_hours', e.target.checked)} />
                Don’t measure outside these hours (±1h)
              </label>
              <p style={{ fontSize: 11, color: 'var(--ind-tx3)', margin: '4px 0 0', lineHeight: 1.6 }}>
                The report compares each desk against this window. Leaving the box ticked also saves CPU
                overnight — the camera is not sampled at all when the office is closed.
              </p>
            </div>

            <details style={{ marginTop: 16 }}>
              <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--ind-tx2)' }}>Fine tuning</summary>
              <div style={{ marginTop: 10, display: 'grid', gap: 10 }}>
                <Field label="Check every" hint="How often the camera is sampled. Occupancy is measured in minutes, so there is nothing to gain below 10s — and every sample costs CPU.">
                  <input type="number" min={2} max={120} value={cfg.sample_secs}
                    onChange={e => patch('sample_secs', Math.max(2, Number(e.target.value) || 10))} style={{ ...sel, width: 80 }} /> s
                </Field>
                <Field label="Confirm after" hint="Samples the seat must look occupied before the clock starts — stops someone walking past a desk from registering.">
                  <input type="number" min={1} max={10} value={cfg.confirm_samples}
                    onChange={e => patch('confirm_samples', Math.max(1, Number(e.target.value) || 2))} style={{ ...sel, width: 80 }} /> samples
                </Field>
                <Field label="Ignore breaks under" hint="A shorter absence keeps the session open so the timeline is not shredded into fragments. The seconds are still excluded from time-at-desk and reported as away.">
                  <input type="number" min={0} max={60} value={Math.round((cfg.merge_gap_secs ?? 180) / 60)}
                    onChange={e => patch('merge_gap_secs', Math.max(0, Number(e.target.value) || 0) * 60)} style={{ ...sel, width: 80 }} /> min
                </Field>
                <Field label="Detection confidence" hint="Lower finds partly hidden people (behind a monitor) but risks false positives. 0.35 suits a desk view.">
                  <input type="number" min={0.05} max={0.9} step={0.05} value={cfg.conf}
                    onChange={e => patch('conf', Number(e.target.value) || 0.35)} style={{ ...sel, width: 80 }} />
                </Field>
              </div>
            </details>

            <div style={{ display: 'flex', gap: 8, marginTop: 18, flexWrap: 'wrap' }}>
                <button className="ind-pill on" onClick={save} disabled={saving}>
                  {saving ? 'Saving…' : dirty ? '💾 Save seats' : '💾 Save'}
                </button>
                {on && <button className="ind-pill" onClick={disable}>Switch off</button>}
                <button className="ind-pill" onClick={() => nav('/industrial/seat-report')}>
                📊 Open report
              </button>
            </div>
          </div>
        </div>

        <p style={{ fontSize: 11, color: 'var(--ind-tx3)', marginTop: 16, lineHeight: 1.7, maxWidth: 780 }}>
          <b>How the measurement works.</b> This runs its own check on a timer rather than reusing the
          normal person detection, which only wakes on movement — a person sitting still at a desk would
          otherwise disappear within a minute of sitting down. Seat occupancy raises no alerts and records
          no video; it only writes how long each seat was occupied.
        </p>
      </div>
    </IndustrialLayout>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ind-tx2)' }}>
        <span style={{ minWidth: 150 }}>{label}</span>{children}
      </div>
      {hint && <div style={{ fontSize: 10.5, color: 'var(--ind-tx3)', marginTop: 3, lineHeight: 1.5 }}>{hint}</div>}
    </div>
  );
}

const sel = { padding: '6px 8px', borderRadius: 6, border: '1px solid var(--ind-line)', background: 'var(--ind-ink)', color: 'var(--ind-tx)', fontSize: 12 };
const lbl = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ind-tx2)', marginTop: 8 };
